import type { SiteConfig } from './types';

const CDN_BASE = 'https://cdn.travogue.bg/assets';

export const travogueConfig: SiteConfig = {
  id: 'travogue',

  // ---------------------------------------------------------------------------
  // ТЕМА (CSS custom properties)
  // ---------------------------------------------------------------------------
  theme: {
    primary50: '#f0f9f6',
    primary100: '#dcf2ed',
    primary200: '#b8e4d9',
    primary300: '#85d1c0',
    primary400: '#4db8a3',
    primary500: '#2c543d', // Main brand color
    primary600: '#137c58',
    primary700: '#156b5f',
    primary800: '#0f5a4f',
    primary900: '#1a6b5f',

    primaryGlow: 'rgba(31, 124, 110, 0.3)',
    accentColor: 'hsl(350, 100%, 90%)',
    themeColor: '#FF69B4',

    // Брандовото зелено; бялото върху него е далеч над прага.
    switchAccent: '#2c543d',
    switchAccentDeep: '#234433',
    switchAccentInk: '#ffffff',

    backgroundPalette: [
      'hsl(350, 22%, 62%)',
      'hsl(200, 22%, 62%)',
      'hsl(280, 20%, 60%)',
      'hsl(160, 22%, 58%)',
      'hsl(30, 25%, 62%)',
      'hsl(320, 20%, 60%)',
      'hsl(180, 22%, 60%)',
      'hsl(45, 24%, 62%)',
      'hsl(260, 20%, 58%)',
      'hsl(140, 22%, 60%)',
    ],
    circleBig: { size: 1300, opacity: 0.8 },
    circleSmall: { size: 700, opacity: 0.2 },
  },

  // ---------------------------------------------------------------------------
  // БРАНДИНГ
  // ---------------------------------------------------------------------------
  branding: {
    name: 'Travogue',
    // Езикът тук трябва да е български: този текст отива в Organization/WebSite
    // JSON-LD, в PWA манифеста и е fallback заглавие за страници без админ SEO ред.
    // Google пренаписва заглавия при разминаване между съдържание и метаданни.
    tagline: 'Фотокнига от твоето пътуване',
    description:
      'Превърни снимките от пътуванията си в истинска фотокнига с твърди корици. Авторски дизайн за над 100 дестинации, твоите снимки и текстове, готова книга у дома.',
    keywords:
      'фотокнига, фото книга, фотокнига от пътуване, фотоалбум, фотокнига със снимки, персонализиран албум, книга със спомени, албум от пътуване, дневник от пътуване, подарък за пътешественик',
    locale: 'bg_BG',
    lang: 'bg',

    cdnBase: 'https://cdn.travogue.bg',

    logoUrl: `${CDN_BASE}/logos/logo.svg`,
    logoMobileUrl: `${CDN_BASE}/logos/logo-mob.svg`,
    logoMobileDarkUrl: `${CDN_BASE}/logos/logo-mobile.svg`,
    logoMobileLightUrl: `${CDN_BASE}/logos/logo-mobile-light.svg`,
    logoAlt: 'Travogue Logo',

    pwaIcon192: `${CDN_BASE}/favicons/web-app-manifest-192x192.png`,
    pwaIcon512: `${CDN_BASE}/favicons/web-app-manifest-512x512.png`,
    pwaCategories: ['photo', 'travel', 'lifestyle'],

    // Качи в R2 под assets/: favicon-96x96.png, favicon.svg, favicon.ico, apple-touch-icon.png, site.webmanifest (BabyVogue: assets/babyvogue/).
    faviconBundle: {
      png96: `${CDN_BASE}/favicons/favicon-96x96.png`,
      svg: `${CDN_BASE}/favicons/favicon.svg`,
      ico: `${CDN_BASE}/favicons/favicon.ico`,
      appleTouch180: `${CDN_BASE}/favicons/apple-touch-icon.png`,
      webManifest: '/api/manifest',
    },

    fontFamily: '"Oddval", sans-serif',
    fontFamilyName: 'Oddval',
  },

  // ---------------------------------------------------------------------------
  // КОНТАКТ
  // ---------------------------------------------------------------------------
  contact: {
    email: 'travoguebook@gmail.com',
    phone: '+359897686203',
    socialFacebook: 'https://www.facebook.com/share/1CncgGo4HV/?mibextid=wwXIfr',
    socialInstagram: 'https://www.instagram.com/travoguebooks',
  },

  // ---------------------------------------------------------------------------
  // ДОМЕЙН
  // ---------------------------------------------------------------------------
  domain: {
    production: 'www.travogue.bg',
    staging: 'staging.travogue.bg',
    metadataBase: 'https://www.travogue.bg',
    googleVerification: '8GKm0lcQsII90Ehx4RSCOvvTbFpUDlmzNvSjKd3UVOk',
  },

  // ---------------------------------------------------------------------------
  // ФИРМЕНИ / ПРАВНИ ДАННИ
  // ---------------------------------------------------------------------------
  legal: {
    companyName: process.env.COMPANY_NAME || '',
    companyFullName: '„Процесинг Партнърс" ООД',
    eik: process.env.EIK || '',
    address: process.env.COMPANY_ADDRESS || '',
    legalEmail: 'info.processingpartners@gmail.com',
    legalPhone: '+359897686203',
    vatRegistered: process.env.VAT_REGISTERED === 'yes',
    vatRate: parseFloat(process.env.VAT_RATE || '20'),
  },

  // ---------------------------------------------------------------------------
  // ПЛАЩАНЕ (DSK)
  // ---------------------------------------------------------------------------
  payment: {
    merchantLogin:
      process.env.NEXT_PUBLIC_DSK_USERNAME || process.env.DSK_USERNAME || 'travoguebook',
    merchantName: process.env.NEXT_PUBLIC_DSK_MERCHANT_NAME || 'Travogue',
    merchantId: 'merchant.travoguebook.com',
    currency: parseInt(process.env.DSK_CURRENCY || '978', 10),
  },

  // ---------------------------------------------------------------------------
  // СЪДЪРЖАНИЕ (дефолтни текстове)
  // ---------------------------------------------------------------------------
  content: {
    aboutIntroImageAlt: 'Персонализирани фотоалбуми от Travogue',
    copyrightYear: new Date().getFullYear(),
    deliveryDescription: 'Персонализирана книга',
    defaultCoverTextColor: '#FF69B4',
    taxonomyNoun: 'дестинация',
    taxonomyNounPlural: 'дестинации',
    offlineTitle: 'Няма интернет връзка',
    offlineDescription: 'Моля, проверете интернет връзката си и опитайте отново.',
    offlineRetryButton: 'Опитай отново',
    aboutAssets: {
      introImage: `${CDN_BASE}/about%20us/general.png`,
      packagingImage: `${CDN_BASE}/about%20us/package1.png`,
      heroImage: `${CDN_BASE}/images/template.png`,
      stepImages: [
        `${CDN_BASE}/about%20us/step1.svg`,
        `${CDN_BASE}/about%20us/step2.svg`,
        `${CDN_BASE}/about%20us/step3.svg`,
        `${CDN_BASE}/about%20us/step4.svg`,
      ],
      featurePlaceholder: `${CDN_BASE}/images/template.png`,
      teamPlaceholder: `${CDN_BASE}/images/template.png`,
    },
  },

  // ---------------------------------------------------------------------------
  // SHARED ASSETS (fallback URLs за map, textures, icons)
  // ---------------------------------------------------------------------------
  assets: {
    mapFallback: `${CDN_BASE}/map/map.svg`,
    textureFallback: `${CDN_BASE}/textures/noise.webp`,
    shelfBackground: `${CDN_BASE}/backgrounds/shelf.png`,
    googleIcon: `${CDN_BASE}/icons/google-icon.svg`,
    facebookIcon: `${CDN_BASE}/icons/facebook-icon.svg`,
    ogImage: `${CDN_BASE}/og/og-image.png`,
  },
};
